import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

interface BottomBarContextValue {
  /** True while a page has claimed the bottom bar for a contextual toolbar. */
  claimed: boolean;
  setClaimed: (v: boolean) => void;
}

const BottomBarContext = createContext<BottomBarContextValue>({
  claimed: false,
  setClaimed: () => {},
});

/**
 * Lets a page take over the one bottom bar a phone has room for.
 *
 * Without this the dashboard's bulk toolbar and the tab bar both anchor to the
 * bottom edge and fight: the toolbar sat at `bottom-4` with `z-20` against the
 * bar's `z-40`, so its lower rows rendered *behind* the tab bar.
 */
export function BottomBarProvider({ children }: { children: ReactNode }) {
  const [claimed, setClaimed] = useState(false);
  const value = useMemo(() => ({ claimed, setClaimed }), [claimed]);
  return <BottomBarContext.Provider value={value}>{children}</BottomBarContext.Provider>;
}

export const useBottomBar = () => useContext(BottomBarContext);

/**
 * Claim the bottom bar while `active`. The tab bar unmounts rather than hiding
 * underneath, so its links leave the tab order and the screen reader tree too.
 */
export function useClaimBottomBar(active: boolean) {
  const { setClaimed } = useBottomBar();
  useEffect(() => {
    setClaimed(active);
    return () => setClaimed(false);
  }, [active, setClaimed]);
}
